import * as THREE from "three";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { SELECTION_FRAME_COLOR } from "./constants";
import { fatLineMaterial } from "./fatLine";

const FRAME_RENDER_ORDER = 10_000;
const MIN_FRAME_SIZE = 1e-3;

const UNIT_BOX_CORNERS: ReadonlyArray<readonly [number, number, number]> = [
  [-0.5, -0.5, -0.5], [0.5, -0.5, -0.5], [0.5, 0.5, -0.5], [-0.5, 0.5, -0.5],
  [-0.5, -0.5, 0.5], [0.5, -0.5, 0.5], [0.5, 0.5, 0.5], [-0.5, 0.5, 0.5],
];
const UNIT_BOX_EDGES: ReadonlyArray<readonly [number, number]> = [
  [0, 1], [1, 2], [2, 3], [3, 0],
  [4, 5], [5, 6], [6, 7], [7, 4],
  [0, 4], [1, 5], [2, 6], [3, 7],
];

function buildUnitBoxGeometry(): LineSegmentsGeometry {
  const positions: number[] = [];
  for (const [a, b] of UNIT_BOX_EDGES) {
    positions.push(...UNIT_BOX_CORNERS[a], ...UNIT_BOX_CORNERS[b]);
  }
  const geometry = new LineSegmentsGeometry();
  geometry.setPositions(positions);
  return geometry;
}

const unitBoxGeometry = buildUnitBoxGeometry();

const localBox = new THREE.Box3();
const childBox = new THREE.Box3();
const rootInverse = new THREE.Matrix4();
const childToRoot = new THREE.Matrix4();
const boxCenter = new THREE.Vector3();
const boxSize = new THREE.Vector3();

export interface FrameRequest {
  mesh: THREE.Mesh;
  color: THREE.Color;
}

interface FrameEntry {
  line: LineSegments2;
  material: LineMaterial;
}

/**
 * The oriented bounding box of a mesh and its descendants, expressed in the
 * mesh's own local frame so a frame parented to it follows position / rotation /
 * scale automatically. Each descendant geometry's box is mapped through
 * rootInverse * childWorld, which cancels the root's world transform regardless
 * of any stale ancestor matrices (the shared factor divides out). Non-mesh
 * children (an attached frame from a prior selection) are skipped.
 */
function localBounds(root: THREE.Mesh): boolean {
  root.updateWorldMatrix(false, true);
  rootInverse.copy(root.matrixWorld).invert();
  localBox.makeEmpty();
  root.traverse((child: THREE.Object3D): void => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry) {
      return;
    }
    if (!mesh.geometry.boundingBox) {
      mesh.geometry.computeBoundingBox();
    }
    const bb = mesh.geometry.boundingBox;
    if (!bb) {
      return;
    }
    childBox.copy(bb);
    childToRoot.multiplyMatrices(rootInverse, mesh.matrixWorld);
    childBox.applyMatrix4(childToRoot);
    localBox.union(childBox);
  });
  return !localBox.isEmpty();
}

/**
 * Draws a fixed-width OBB wireframe around each requested mesh by parenting a
 * unit-box LineSegments2 to it, positioned / scaled to the mesh's local-frame
 * OBB so it follows the mesh's live world transform (and per-frame group-box
 * resizing) without per-frame recompute — the set is rebuilt only when a request
 * changes. LineMaterial gives a screen-space line width (WebGL's plain line
 * width is clamped to 1px); depthTest:false + a high renderOrder draws on top.
 * Replaces the shared OutlinePass that previously carried selection /
 * wire-target / group highlights. The resolution uniform must track the canvas
 * size for correct pixel width, so `setResolution` is wired to resize.
 */
export class SelectionFrames {
  private readonly pool: FrameEntry[] = [];
  private requests: FrameRequest[] = [];
  private enabled = true;
  private readonly resolution = new THREE.Vector2(1, 1);

  apply(requests: FrameRequest[]): void {
    this.requests = requests;
    this.render();
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    this.render();
  }

  setResolution(width: number, height: number): void {
    this.resolution.set(width, height);
    for (const entry of this.pool) {
      entry.material.resolution.copy(this.resolution);
    }
  }

  count(): number {
    return this.enabled ? this.requests.length : 0;
  }

  private render(): void {
    const requests = this.enabled ? this.requests : [];
    while (this.pool.length < requests.length) {
      this.pool.push(this.createFrame());
    }
    for (let i = 0; i < this.pool.length; i++) {
      const entry = this.pool[i];
      if (i < requests.length && this.bind(entry, requests[i])) {
        continue;
      }
      this.hide(entry);
    }
  }

  private bind(entry: FrameEntry, request: FrameRequest): boolean {
    if (!localBounds(request.mesh)) {
      return false;
    }
    localBox.getCenter(boxCenter);
    localBox.getSize(boxSize);
    entry.material.color.copy(request.color);
    entry.line.position.copy(boxCenter);
    entry.line.scale.set(
      Math.max(boxSize.x, MIN_FRAME_SIZE),
      Math.max(boxSize.y, MIN_FRAME_SIZE),
      Math.max(boxSize.z, MIN_FRAME_SIZE),
    );
    if (entry.line.parent !== request.mesh) {
      entry.line.removeFromParent();
      request.mesh.add(entry.line);
    }
    entry.line.visible = true;
    return true;
  }

  private hide(entry: FrameEntry): void {
    entry.line.visible = false;
    entry.line.removeFromParent();
  }

  private createFrame(): FrameEntry {
    const material = fatLineMaterial(SELECTION_FRAME_COLOR);
    material.resolution.copy(this.resolution);
    const line = new LineSegments2(unitBoxGeometry, material);
    line.renderOrder = FRAME_RENDER_ORDER;
    line.frustumCulled = false;
    line.visible = false;
    line.userData.selectionFrame = true;
    return { line, material };
  }
}
